// Visão do cupom aplicado no carrinho nativo (2026-10-16). Módulo PURO,
// compartilhado entre o servidor (que calcula) e o navegador (que exibe e
// usa no resumo do checkout). Dinheiro em centavos como string de dígitos,
// igual ao restante de NativeCartView.

import type { CouponKind, CouponPixMode } from "./couponMath.ts";

export interface NativeCartCouponView {
  code: string;
  description: string | null;
  kind: CouponKind;
  /** true = vale agora e gera desconto (itens) ou frete grátis. */
  applied: boolean;
  /** Desconto nos produtos (centavos). "0" para frete grátis ou cupom sem efeito. */
  itemDiscountMinor: string;
  freeShipping: boolean;
  /** Teto do desconto no frete (frete grátis); null = sem teto. */
  shippingDiscountCapMinor: string | null;
  pixMode: CouponPixMode;
  /** Motivo (pt-BR) quando applied=false, ex.: "Faltam R$ 12,00...". */
  message: string | null;
}

const DIGITS = /^\d{1,15}$/;
const KINDS = new Set(["percentage", "fixed_cart", "free_shipping"]);
const PIX_MODES = new Set(["after_coupon", "not_combinable"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Validação defensiva da visão vinda da rede (null = ausente/malformada). */
export function parseNativeCartCouponView(value: unknown): NativeCartCouponView | null {
  if (!isRecord(value)) return null;
  if (
    typeof value.code !== "string" || !value.code ||
    !(value.description === null || typeof value.description === "string") ||
    typeof value.kind !== "string" || !KINDS.has(value.kind) ||
    typeof value.applied !== "boolean" ||
    typeof value.itemDiscountMinor !== "string" || !DIGITS.test(value.itemDiscountMinor) ||
    typeof value.freeShipping !== "boolean" ||
    !(value.shippingDiscountCapMinor === null || (typeof value.shippingDiscountCapMinor === "string" && DIGITS.test(value.shippingDiscountCapMinor))) ||
    typeof value.pixMode !== "string" || !PIX_MODES.has(value.pixMode) ||
    !(value.message === null || typeof value.message === "string")
  ) {
    return null;
  }
  return value as unknown as NativeCartCouponView;
}

/** Desconto no frete que o cupom daria para um frete escolhido (centavos). */
export function couponShippingDiscountFor(coupon: NativeCartCouponView | null | undefined, shippingMinor: number | null): number {
  if (!coupon?.applied || !coupon.freeShipping || shippingMinor === null) return 0;
  const shipping = Math.max(0, Math.trunc(shippingMinor));
  if (coupon.shippingDiscountCapMinor === null) return shipping;
  return Math.min(shipping, Number(coupon.shippingDiscountCapMinor));
}

export function couponItemDiscountFor(coupon: NativeCartCouponView | null | undefined): number {
  if (!coupon?.applied) return 0;
  return Number(coupon.itemDiscountMinor);
}

/**
 * Código que o checkout espera ver aplicado no pedido, dado o frete
 * escolhido ("" = nenhum). Frete grátis com frete zero (retirada, entrega
 * grátis) não gera desconto e não conta como aplicado -- mesma regra de
 * resolveCheckoutCouponFromRule no servidor.
 */
export function expectedCheckoutCouponCode(coupon: NativeCartCouponView | null | undefined, shippingMinor: number | null): string {
  if (!coupon?.applied) return "";
  if (couponItemDiscountFor(coupon) + couponShippingDiscountFor(coupon, shippingMinor ?? 0) <= 0) return "";
  return coupon.code.toUpperCase();
}
